import React, { useState, useEffect } from "react";
import { customFetch } from "../../utils/Auth";
import { environment } from "../../config/environment";

const CreateTestPatients = () => {
  const [allDoctors, setAllDoctors] = useState([]);
  const [doctorSearch, setDoctorSearch] = useState("");
  const [filteredDoctors, setFilteredDoctors] = useState([]);
  const [selectedDoctor, setSelectedDoctor] = useState(null);
  const [date, setDate] = useState(new Date().toLocaleDateString("en-US")); // YYYY-MM-DD 
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(false);

  // Fetch all doctors when component mounts
  useEffect(() => {
    (async () => {
      try {
        const response = await customFetch(
          `${environment.apiUrl}/doctors/all-doctors`,
          { method: "GET" }
        );
        const data = await response.json();
        setAllDoctors(Array.isArray(data) ? data : []);
      } catch (err) {
        console.error("Error fetching doctors:", err);
        setAllDoctors([]);
      }
    })();
  }, []);

  // Filter doctors as user types
  useEffect(() => {
    if (doctorSearch.trim()) {
      const search = doctorSearch.trim().toLowerCase();
      setFilteredDoctors(
        allDoctors.filter(doc => {
          const name = doc.user_name?.toLowerCase() || "";
          const id = doc.user_id?.toLowerCase() || "";
          const email = doc.user_email?.toLowerCase() || "";
          const npi = doc.npi?.toLowerCase() || "";
          return (
            name.includes(search) ||
            id.includes(search) ||
            email.includes(search) ||
            npi.includes(search)
          );
        })
      );
    } else {
      setFilteredDoctors([]);
    }
  }, [doctorSearch, allDoctors]);

  const createTestPatients = async () => {
    try {
      setLoading(true);
      setMessage("");

      if (!selectedDoctor) {
        setMessage("❌ Please select a doctor");
        return;
      }

      const response = await fetch(
        `${environment.apiUrl}/patients/create-va-patients`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            token: localStorage.getItem("token"),
          },
          body: JSON.stringify({
            userId: selectedDoctor.user_id,
            date: date || undefined,
          }),
        }
      );

      const data = await response.json();

      if (response.ok) {
        setMessage(`✅ Created 3 test patients for ${data.date}`);
        console.log("Created patients:", data.patients);
      } else {
        setMessage(`❌ Error: ${data.error}`);
      }
    } catch (err) {
      console.error("Error creating test patients:", err);
      setMessage(`❌ Error: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-2xl mx-auto">
      <div className="bg-white shadow-md rounded-lg p-6 mb-4">
        {/* Doctor Selection */}
        <div className="mb-4">
          <label
            htmlFor="doctorSearch"
            className="block text-sm font-medium text-gray-700 mb-2"
          >
            Select Doctor
          </label>
          <input
            id="doctorSearch"
            type="text"
            autoComplete="off"
            value={doctorSearch}
            onChange={(e) => setDoctorSearch(e.target.value)}
            placeholder="Type to search for a doctor..."
            className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-ktx-purple focus:border-ktx-purple"
          />

          {/* Selected Doctor Display */}
          {selectedDoctor && !doctorSearch && (
            <div className="mt-2 p-3 bg-purple-50 border border-purple-200 rounded-md">
              <div className="flex justify-between items-start">
                <div>
                  <div className="font-semibold text-gray-900">
                    {selectedDoctor.user_name && selectedDoctor.user_name !== 'placeholder'
                      ? selectedDoctor.user_name
                      : 'No name set'}
                  </div>
                  <div className="text-sm text-gray-600">
                    {selectedDoctor.user_email && (
                      <div>Email: {selectedDoctor.user_email}</div>
                    )}
                    {selectedDoctor.npi && (
                      <div>NPI: {selectedDoctor.npi}</div>
                    )}
                    <div className="text-xs text-gray-500">
                      ID: {selectedDoctor.user_id}
                    </div>
                  </div>
                </div>
                <button
                  onClick={() => {
                    setSelectedDoctor(null);
                    setDoctorSearch("");
                  }}
                  className="text-gray-500 hover:text-gray-700"
                >
                  ✕
                </button>
              </div>
            </div>
          )}

          {/* Doctor Dropdown */}
          {doctorSearch && filteredDoctors.length > 0 && (
            <div className="mt-2 max-h-56 overflow-y-auto border border-gray-200 rounded-md bg-white shadow-lg z-10">
              {filteredDoctors.map((doc) => (
                <div
                  key={doc.user_id}
                  className={`px-4 py-2 cursor-pointer hover:bg-purple-100 ${selectedDoctor && selectedDoctor.user_id === doc.user_id
                    ? "bg-purple-200"
                    : ""
                    }`}
                  onClick={() => {
                    setSelectedDoctor(doc);
                    setDoctorSearch("");
                  }}
                >
                  <div className="font-semibold text-gray-900">
                    {doc.user_name && doc.user_name !== 'placeholder'
                      ? doc.user_name
                      : 'No name set'}
                  </div>
                  <div className="text-xs text-gray-600">
                    {doc.user_email && (
                      <span className="mr-3">📧 {doc.user_email}</span>
                    )}
                    {doc.npi && <span>🔢 NPI: {doc.npi}</span>}
                  </div>
                  <div className="text-xs text-gray-500">ID: {doc.user_id}</div>
                </div>
              ))}
            </div>
          )}

          {doctorSearch && filteredDoctors.length === 0 && (
            <div className="mt-2 px-4 py-2 text-gray-400 border border-gray-200 rounded-md">
              No doctors found.
            </div>
          )}
        </div>

        {/* Date Selection */}
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Date for Appointments
          </label>
          <input
            type="date"
            value={date}
            onChange={(e) => setDate(e.target.value)}
            className="w-full p-2 border border-gray-300 rounded-md"
          />
        </div>

        {/* Action Buttons */}
        <div className="flex gap-4">
          <button
            onClick={createTestPatients}
            disabled={loading || !selectedDoctor || !date}
            className="flex-1 bg-ktx-purple text-white py-2 px-4 rounded-md hover:bg-ktx-navy disabled:bg-gray-400 disabled:cursor-not-allowed"
          >
            {loading ? "Creating..." : "Create 3 Test Patients"}
          </button>
        </div>

        {/* Message Display */}
        {message && (
          <div
            className={`mt-4 p-3 rounded-md ${message.includes("✅")
              ? "bg-green-100 text-green-800"
              : "bg-red-100 text-red-800"
              }`}
          >
            {message}
          </div>
        )}
      </div>

      {/* Test Patients Info */}
      <div className="bg-gray-100 p-4 rounded-md">
        <h3 className="font-semibold mb-2">Test Patients Info:</h3>
        <ul className="text-sm space-y-1">
          <li>• MHVZZVISNFIFTEEN,TEST PATIENTM (MRN: 1017560839V547667)</li>
          <li>• QQQVAINTERFACE,IFC (MRN: 1055152953V564703)</li>
          <li>• QQQVAINTERFACE,IFC TWO (MRN: 1061312409V524429)</li>
        </ul>
      </div>
    </div>
  );
};

export default CreateTestPatients;
